<div class="page-header">
  <div class="header-content">
    <div class="header-text">
      <h1>Access Management</h1>
    </div>
  </div>
  <button *ngIf="userPrivileges.has('event_access_management.update')" class="create-role-btn" (click)="openRoleSelection()" [disabled]="isLoading">
    Assign Role
  </button>
</div>

<div class="access-management-container">
  <!-- Global API Loading Spinner -->
  <div *ngIf="isLoading" class="loading-state">
    <div class="loading-spinner">
      <i class="pi pi-spin pi-spinner"></i>
      <p>Loading...</p>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="currentView === 'empty' && !isLoading" class="empty-state">
    <div class="empty-state-card">
      <div class="empty-state-icon">
        <i class="pi pi-shield"></i>
      </div>
      <h2>Start with your first role</h2>
      <p>Create roles to organize your team members and manage access permissions effectively. Each role can have specific permissions and contain multiple team members.</p>
      <button *ngIf="userPrivileges.has('event_access_management.update')" class="create-first-role-btn" (click)="openRoleSelection()">
        <i class="pi pi-plus"></i>
        Assign Your First Role
      </button>
    </div>
  </div>

  <!-- Role Detail View -->
  <div *ngIf="currentView === 'role-detail' && assignedRoles.length > 0 && !isLoading" class="role-detail-view">
    <!-- Role Card -->
    <div *ngFor="let role of assignedRoles" class="role-card">
      <div class="role-header">
        <div class="role-info">
          <div class="role-option-icon role-icon-blue">
            <i class="pi pi-users"></i>
          </div>
          <div class="role-details">
            <h2>{{role.name}}</h2>
            <p>{{role.users.length}} members</p>
          </div>
        </div>
        <div class="role-actions">
          <button *ngIf="userPrivileges.has('event_access_management.update')" class="delete-btn" (click)="confirmDeleteRole(role)" [disabled]="isLoading">
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>

      <div class="members-section">
        <div class="members-header">
          <h3>Available Members</h3>
        </div>
        <!-- Empty Members State -->
        <div *ngIf="role.users.length === 0" class="empty-members">
          <div class="empty-members-icon">
            <i class="pi pi-users"></i>
          </div>
          <h4>No members assigned yet</h4>
          <p>Start building your team by adding members to this role</p>
          <button class="add-first-member-btn" (click)="openAddMembers(role)">
            <i class="pi pi-plus"></i>
            Add First Member
          </button>
        </div>
        <!-- Members List -->
        <div class="members-list" *ngIf="role.users.length > 0">
          <div *ngFor="let member of role.users" class="member-item">
            <div class="member-avatar">
              <span>{{ member.first_name[0] }}{{ member.last_name[0] }}</span>
            </div>
            <div class="member-info">
              <h4>{{ member.first_name }} {{ member.last_name }}</h4>
              <p>{{member.email}}</p>
            </div>
            <div class="member-actions">
              <button type="button"
              *ngIf="userPrivileges.has('event_access_management.update')"
                class="delete-member-btn"
                (click)="confirmDeleteMember(member)"
                [disabled]="isDeletingMember"
                title="Remove member from {{ role.name }}">
                <i class="pi pi-trash" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
        <!-- Assign Members Button -->
        <button class="assign-members-btn" (click)="openAddMembers(role)" *ngIf="role.users.length > 0 && userPrivileges.has('event_access_management.update')">
          <i class="pi pi-users"></i>
          Assign Members
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Member Modal -->
  <div class="modal-backdrop" *ngIf="showDeleteMemberModal" (click)="closeDeleteMemberModal()">
    <div class="modal-container delete-member-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Remove Member</h3>
        <button type="button" class="close-btn" (click)="closeDeleteMemberModal()">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="warning-icon">
          <i class="fa fa-exclamation-triangle"></i>
        </div>
        <p class="confirmation-text">
          Are you sure you want to remove <strong>{{ memberToDelete?.first_name }} {{ memberToDelete?.last_name }}</strong> from the
          <strong>{{ selectedRole?.name }}</strong> role?
        </p>
        <p class="sub-text">This action cannot be undone.</p>
      </div>
      <div class="modal-footer">
        <button type="button"
          class="btn btn-secondary"
          (click)="closeDeleteMemberModal()"
          [disabled]="isDeletingMember">
          Cancel
        </button>
        <button type="button"
          class="btn btn-danger"
          (click)="deleteMember()"
          [disabled]="isDeletingMember">
          <span *ngIf="isDeletingMember" class="spinner"></span>
          {{ isDeletingMember ? 'Removing...' : 'Remove Member' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Role Modal -->
  <div class="modal-overlay" *ngIf="showDeleteConfirmModal" (click)="closeDeleteConfirmModal()">
    <div class="modal-content delete-confirmation-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Delete Role</h3>
        <button class="close-btn" (click)="closeDeleteConfirmModal()">
          <i class="pi pi-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="warning-icon">
          <i class="pi pi-exclamation-triangle"></i>
        </div>
        <p>Are you sure you want to delete the role <strong>"{{roleToDelete?.name}}"</strong>?</p>
        <p class="warning-text">This action cannot be undone and will remove access for all {{roleToDelete?.users?.length || 0}} members assigned to this role.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" (click)="closeDeleteConfirmModal()" [disabled]="isDeleting">
          Cancel
        </button>
        <button class="btn btn-danger" (click)="deleteRole()" [disabled]="isDeleting">
          <span *ngIf="isDeleting" class="spinner"></span>
          {{isDeleting ? 'Deleting...' : 'Delete Role'}}
        </button>
      </div>
    </div>
  </div>

  <!-- Role Selection Modal -->
  <div *ngIf="showRoleModal" class="modal-overlay" (click)="closeModal()">
    <div class="modal-content role-selection-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title">
          <div class="modal-icon">
            <i class="pi pi-shield"></i>
          </div>
          <div>
            <h2>Select Role to Assign</h2>
            <p>Choose from available roles and immediately add members</p>
          </div>
        </div>
        <button class="close-btn" (click)="closeModal()">
          <i class="pi pi-times"></i>
        </button>
      </div>
      <!-- Loading State for Roles -->
      <div *ngIf="isLoadingRoles" class="roles-loading">
        <div class="loading-spinner">
          <i class="pi pi-spin pi-spinner"></i>
        </div>
      </div>
      <div class="roles-grid" *ngIf="!isLoadingRoles">
        <div *ngFor="let role of getAvailableRoles()"
             class="role-option"
             [class.selected]="selectedRole?.id === role.id"
             [class.disabled]="selectedRole && selectedRole.id !== role.id">
      
          <div class="checkmark-icon" (click)="toggleRoleSelection(role, $event)">
            <i class="pi"
               [ngClass]="selectedRole?.id === role.id ? 'pi-check-circle' : 'pi-circle'"></i>
          </div>
      
          <div class="role-option-icon role-icon-blue">
            <i class="pi pi-users"></i>
          </div>
      
          <div class="role-option-content" (click)="selectRole(role)">
            <h3>{{ role.name }}</h3>
            <p>{{ role.description }}</p>
          </div>
        </div>
      </div>
      
      <div *ngIf="selectedRole && !isLoadingRoles" class="selected-role-details">
        <h3>Selected Role</h3>
        <div class="selected-role-info">
          <div class="role-option-icon role-icon-blue">
            <i class="pi pi-users"></i>
          </div>
          <div class="selected-role-content">
            <h4>{{selectedRole.name}}</h4>
            <p>{{selectedRole.description}}</p>
          </div>
        </div>
      </div>
      <div class="modal-actions" *ngIf="!isLoadingRoles">
        <button class="cancel-btn" (click)="closeModal()">Cancel</button>
        <button class="add-role-btn"
                [disabled]="!selectedRole || getAvailableRoles().length === 0"
                (click)="addSelectedRole()">
          Next: Add Members
        </button>
      </div>
    </div>
  </div>

  <!-- Add Members Modal -->
  <div *ngIf="showAddMembersModal" class="modal-overlay" (click)="closeModal()">
    <div class="modal-content add-members-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div class="modal-title">
          <button class="back-btn" *ngIf="cameFromRoleSelection" (click)="goBack()">
            <i class="pi pi-arrow-left"></i>
          </button>
          <div>
            <h2>Add Members to {{selectedRole?.name}}</h2>
            <p>Select team members from the {{selectedRole?.name}} role</p>
          </div>
        </div>
        <button class="close-btn" (click)="closeModal()">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Role Info Card -->
      <div class="role-info-card">
        <div class="role-option-icon role-icon-blue">
          <i class="pi pi-users"></i>
        </div>
        <div>
          <h3>{{selectedRole?.name}}</h3>
        </div>
      </div>

      <div class="members-section">
        <div class="section-header">
          <h3 *ngIf="getFilteredMembers().length > 0">
            Available team members ({{getFilteredMembers().length}})
          </h3>
          <p *ngIf="getFilteredMembers().length === 0 && availableTeamMembers.length > 0" class="no-members-message">
            No members available.
          </p>
        </div>

        <!-- Loading Spinner while fetching members -->
        <div *ngIf="isLoadingMembers" class="members-loading">
          <div class="loading-spinner">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters" *ngIf="getFilteredMembers().length > 0 && !isLoadingMembers">
          <div class="search-input">
            <i class="pi pi-search"></i>
            <input type="text"
                   placeholder="Search users"
                   [(ngModel)]="searchTerm">
          </div>
          <div class="select-all-option">
            <label class="checkbox-label">
              <input type="checkbox"
                     [checked]="selectAll && getFilteredMembers().length > 0"
                     (change)="toggleSelectAll($any($event.target).checked)">
              <span class="checkmark"></span>
            </label>
            <span class="select-all-text">Select All ({{getFilteredMembers().length}})</span>
          </div>
        </div>

        <!-- Available Members List -->
        <div class="available-members-list" *ngIf="getFilteredMembers().length > 0 && !isLoadingMembers">
          <div *ngFor="let member of getFilteredMembers()"
            class="available-member-item"
            [class.selected]="selectedMembers.includes(member.id)"
            (click)="toggleMemberSelection(member.id, !selectedMembers.includes(member.id))">
            <label class="checkbox-label" (click)="$event.stopPropagation()">
              <input type="checkbox"
                     [checked]="selectedMembers.includes(member.id)"
                     (change)="toggleMemberSelectionFromEvent(member.id, $event)">
              <span class="checkmark"></span>
            </label>
            <div class="member-avatar">
              <span>{{member.initials}}</span>
            </div>
            <div class="member-info">
              <h4>{{member.name}}</h4>
              <p>{{member.email}}</p>
              <div class="member-meta">
                <span class="department">
                  <i class="pi pi-users"></i>
                  {{member.department}}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- No Team Members at All -->
        <div *ngIf="!isLoadingMembers && getFilteredMembers().length === 0 && availableTeamMembers.length === 0" class="no-members-simple">
          <div class="no-members-icon">
            <i class="pi pi-users"></i>
          </div>
          <div class="no-members-text">
            <h3>No team members available</h3>
            <p>
              All users with the {{selectedRole?.name || 'role'}} role have already been assigned,<br>
              or there are no users with this role.
            </p>
          </div>
          <button class="simple-back-btn" (click)="goBack()">Back to Role Selection</button>
        </div>

        <!-- No Search Results -->
        <div *ngIf="!isLoadingMembers && getFilteredMembers().length === 0 && availableTeamMembers.length > 0 && searchTerm.length > 0" class="no-search-results">
          <div class="empty-members-icon">
            <i class="pi pi-search"></i>
          </div>
          <h4>No matching members found</h4>
        </div>
      </div>
      <div class="modal-footer" *ngIf="availableTeamMembers.length > 0 && !isLoadingMembers">
        <span class="selected-count">{{selectedMembers.length}} members selected</span>
        <div class="footer-actions">
          <button class="cancel-btn" *ngIf="cameFromRoleSelection" (click)="goBack()">Back</button>
          <button class="add-members-btn"
                  [disabled]="selectedMembers.length === 0 || isLoading || getFilteredMembers().length === 0"
                  (click)="addSelectedMembers()">
            <i *ngIf="isLoading" class="pi pi-spin pi-spinner"></i>
            <span *ngIf="!isLoading">Save {{selectedMembers.length}} Members</span>
            <span *ngIf="isLoading">Saving...</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
